import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtButton from "../../components/NtButton";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import NtModal from "../../components/NtModal";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";

/**
 * Wizard list+modal pattern. Fetches via `fetcher(domain)`, renders the items
 * via `renderItem`, and pops a modal with the existing admin create form
 * (NtExtensionNewForm / NtRingGroupNewForm / NtIvrMenuForm) when the user
 * hits the "Add" button. The form's `onCreate` / `onSave` callbacks refresh
 * the list automatically.
 *
 * Props:
 *   icon, title, subtitle           — header copy
 *   addLabel                        — primary button text ("Add extension")
 *   emptyTitle, emptySubtitle       — copy for the empty state
 *   fetcher(domain) → Promise<ApiResponse>
 *   parseItems(response) → array    — extract the array from the response
 *   renderItem(item, index)         — row JSX
 *   renderForm({ onCancel, onCreate, onSave }) → JSX  — modal body
 */
function NtSetupResourceList({
  icon,
  title,
  subtitle,
  addLabel,
  emptyTitle,
  emptySubtitle,
  fetcher,
  parseItems,
  renderItem,
  renderForm,
}) {
  const { theme } = useTheme();
  const context = useContext(UserContext);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const modalRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const domain = context.user?.domain;

  const load = useCallback(async () => {
    if (!domain || !fetcher) return;
    setLoading(true);
    const response = await fetcher(domain);
    const next = response?.ok ? parseItems(response) : [];
    setItems(Array.isArray(next) ? next : []);
    setLoading(false);
  }, [domain, fetcher, parseItems]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = () => modalRef.current?.show();
  const handleCreated = () => {
    modalRef.current?.dismiss();
    load();
  };

  const isEmpty = !loading && items.length === 0;
  const count = items.length;

  return (
    <View>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <View style={styles.iconWrap}>
            <NtMaterialIcon name={icon} size={18} color={theme.brand2} />
          </View>
          <View style={{ flex: 1 }}>
            <NtText style={styles.title}>{title}</NtText>
            {subtitle ? <NtText style={styles.subtitle}>{subtitle}</NtText> : null}
          </View>
          <View style={styles.countPill}>
            <NtText style={styles.countText}>{count}</NtText>
          </View>
        </View>
        {!isEmpty ? (
          <NtButton text={addLabel} icon="plus" onPress={handleAdd} size="sm" />
        ) : null}
      </View>

      {loading ? (
        <View style={styles.loadingWrap}>
          <NtActivityIndicator size="small" color={theme.brand2} />
        </View>
      ) : isEmpty ? (
        <View style={styles.empty}>
          <View style={styles.emptyIcon}>
            <NtMaterialIcon name={icon} size={28} color={theme.brand2} />
          </View>
          <NtText style={styles.emptyTitle}>{emptyTitle}</NtText>
          <NtText style={styles.emptySub}>{emptySubtitle}</NtText>
          <NtButton
            text={addLabel}
            icon="plus"
            onPress={handleAdd}
            containerStyle={{ marginTop: 16 }}
          />
        </View>
      ) : (
        <View style={styles.list}>
          {items.map((item, index) => renderItem(item, index))}
        </View>
      )}

      <NtModal ref={modalRef}>
        {renderForm({
          onCancel: () => modalRef.current?.dismiss(),
          onCreate: handleCreated,
          onSave: handleCreated,
        })}
      </NtModal>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginBottom: 16,
      maxWidth: 720,
    },
    headerLeft: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      flex: 1,
    },
    iconWrap: {
      width: 32,
      height: 32,
      borderRadius: 8,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      alignItems: "center",
      justifyContent: "center",
    },
    title: { fontSize: 14, fontWeight: "700", color: t.text, letterSpacing: -0.1 },
    subtitle: { fontSize: 12, color: t.textDim, marginTop: 2 },
    countPill: {
      paddingHorizontal: 9,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: t.surface3,
      borderWidth: 1,
      borderColor: t.border,
    },
    countText: { fontSize: 11, fontWeight: "700", color: t.textDim },

    loadingWrap: {
      paddingVertical: 24,
      alignItems: "center",
      justifyContent: "center",
    },

    empty: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderStyle: "dashed",
      borderColor: t.border,
      borderRadius: t.radiusCard,
      paddingVertical: 32,
      paddingHorizontal: 24,
      alignItems: "center",
      maxWidth: 720,
    },
    emptyIcon: {
      width: 56,
      height: 56,
      borderRadius: 14,
      backgroundColor: t.pillBg,
      borderWidth: 1,
      borderColor: t.pillBorder,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 14,
    },
    emptyTitle: {
      fontSize: 15,
      fontWeight: "700",
      color: t.text,
      marginBottom: 4,
    },
    emptySub: {
      fontSize: 12.5,
      color: t.textDim,
      textAlign: "center",
      maxWidth: 420,
      lineHeight: 18,
    },

    list: {
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.border,
      borderRadius: t.radiusCard,
      overflow: "hidden",
      maxWidth: 720,
    },
  });

export default NtSetupResourceList;
